import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  People as PeopleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useActiveUsersService } from '../../services/activeUsersService';
import type { ActiveUsersResponse } from '../../types/activeUsers';

const REFRESH_MS = 30_000;

const formatDateTime = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatRelative = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.max(0, Math.floor(diffMs / 60_000));
  if (minutes < 1) return 'Hace un momento';
  if (minutes === 1) return 'Hace 1 min';
  if (minutes < 60) return `Hace ${minutes} min`;
  return formatDateTime(value);
};

const LoggedInUsersSettingsTab: React.FC = () => {
  const { jwtToken } = useAuth();
  const service = useActiveUsersService();
  const serviceRef = useRef(service);
  serviceRef.current = service;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ActiveUsersResponse | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!jwtToken) {
      setError('No hay token de autenticación');
      setLoading(false);
      return;
    }
    if (!silent) setLoading(true);
    setError(null);
    const res = await serviceRef.current.listOnlineUsers();
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        setError('No tiene permiso para ver los usuarios conectados (se requiere rol ADMIN).');
      } else {
        setError(res.error || 'No se pudo cargar la lista de usuarios conectados');
      }
      if (!silent) setData(null);
    } else if (res.data) {
      setData(res.data);
    }
    setLoading(false);
  }, [jwtToken]);

  useEffect(() => {
    void load();
    const id = window.setInterval(() => {
      void load(true);
    }, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const users = data?.users ?? [];
  const windowMinutes = data?.onlineWindowMinutes ?? 5;

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon />
            Usuarios conectados
            <Chip
              size="small"
              color={(data?.count ?? users.length) > 0 ? 'success' : 'default'}
              label={loading && !data ? '…' : String(data?.count ?? users.length)}
            />
          </Typography>
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
            onClick={() => void load()}
            disabled={loading}
          >
            Actualizar
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Personas con la sesión abierta en este momento. Un usuario aparece aquí si tuvo actividad
          en los últimos {windowMinutes} minutos y desaparece al cerrar sesión o al quedar inactivo.
          La lista se actualiza sola cada 30 segundos.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Usuario</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Correo</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Último acceso</TableCell>
                <TableCell>Última actividad</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && !data ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    No hay usuarios conectados en este momento
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.fullName}</TableCell>
                    <TableCell>{user.email || '—'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(user.roles || []).map((role) => (
                          <Chip key={role} size="small" label={role} variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDateTime(user.lastLogin)}</TableCell>
                    <TableCell>{formatRelative(user.lastSeenAt)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default LoggedInUsersSettingsTab;
